import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import {
  selectFeasibilityPayments,
  selectHighestCycle,
  selectPaymenthasNaCycle,
} from "../../../../slices/apheresis";
import FormikControl from "../../../../components/common/FormikControl";
import { fetchManufecturingButtonDropDown, selectDropDownDataMaster } from "../../../../slices/customMaster";

// Normalize a filter value that may have been stored as an array (from handleSubmit)
// back to a scalar for single-select fields
const toScalar = (val) => {
  if (Array.isArray(val))
    return val.length === 1 ? val[0] : val.length === 0 ? "" : val[0];
  return val ?? "";
};

const PaymentdashboardFilter = ({
  onApply,
  onClear,
  initialFilters,
  products = [],
  hospitals = [],
}) => {
  const initialValues = {
    product_id: toScalar(initialFilters?.product_id),
    phase_type: initialFilters?.phase_type || [],
    cycle: initialFilters?.cycle || [],
    payment_status: initialFilters?.payment_status || [],
    hospital_id: toScalar(initialFilters?.hospital_id),
  };
  const dispatch = useDispatch();
const PaymentStatusList = useSelector(selectDropDownDataMaster('payment'));

useEffect(() => {
    dispatch(fetchManufecturingButtonDropDown('payment'));
}, []);
  const highestCycle = useSelector(selectHighestCycle);
  const hasNaCycle = useSelector(selectPaymenthasNaCycle);
  const feasibilityPayments = useSelector(selectFeasibilityPayments);
  const [cycleOptions, setCycleOptions] = useState([]);

  // Stable options: only update when we get non-empty data, so options survive filtered/empty responses
  const [stableProductOptions, setStableProductOptions] = useState([]);
  const [stableHospitalOptions, setStableHospitalOptions] = useState([]);

  const rows = Array.isArray(feasibilityPayments?.items)
    ? feasibilityPayments.items
    : Array.isArray(feasibilityPayments?.data?.items)
    ? feasibilityPayments.data.items
    : Array.isArray(feasibilityPayments?.data)
    ? feasibilityPayments.data
    : [];

  const typeOptions = [
    { value: "PMS", label: "PMS" },
    { value: "Commercial", label: "Commercial" },
    { value: "Trial", label: "Trial" },
  ];

  const freshProductOptions = useMemo(() => {
    if (products && products.length > 0) return products;
    const map = new Map();
    rows.forEach((row) => {
      if (row?.product_id != null && row?.product) {
        if (!map.has(row.product_id)) {
          map.set(row.product_id, {
            value: row.product_id,
            label: row.product,
          });
        }
      }
    });
    return Array.from(map.values());
  }, [products, rows]);

  const freshHospitalOptions = useMemo(() => {
    if (hospitals && hospitals.length > 0) return hospitals;
    const map = new Map();
    rows.forEach((row) => {
      (row?.hospitals || []).forEach((h) => {
        if (h?.id != null && h?.name && !map.has(h.id)) {
          map.set(h.id, { value: h.id, label: h.name });
        }
      });
    });
    return Array.from(map.values());
  }, [hospitals, rows]);

  // Keep stable options — only replace when fresh data is non-empty
  useEffect(() => {
    if (freshProductOptions.length > 0)
      setStableProductOptions(freshProductOptions);
  }, [freshProductOptions]);

  useEffect(() => {
    if (freshHospitalOptions.length > 0)
      setStableHospitalOptions(freshHospitalOptions);
  }, [freshHospitalOptions]);

  const productOptions = stableProductOptions;
  const hospitalOptions = stableHospitalOptions;

  const generateCycleOptions = (highestCycle, hasNaCycle) => {
    const options = [];
    if (hasNaCycle === 1) {
      options.push({
        value: "N/A",
        label: "N/A",
      });
    }
    const match = highestCycle?.match(/\d+/);
    if (match) {
      const max = parseInt(match[0], 10);
      for (let i = 1; i <= max; i++) {
        options.push({
          value: `Cycle ${i}`,
          label: `Cycle ${i}`,
        });
      }
    }
    return options;
  };
  useEffect(() => {
    const options = generateCycleOptions(highestCycle, hasNaCycle);
    setCycleOptions(options);
  }, [hasNaCycle, highestCycle]);
  const handleCycleChange = (event) => {
    const selectedValues =
      typeof event.target.value === "string"
        ? event.target.value.split(",")
        : event.target.value;

    const filtered = Array.isArray(selectedValues)
      ? selectedValues.filter(Boolean)
      : [];
    formik.setFieldValue("cycle", filtered);
  };

  const formOptions = [
    {
      label: "Product",
      type: "select",
      name: "product_id",
      placeholder: "Select Product",
      options: productOptions,
    },
    {
      label: "Type",
      type: "checkbox",
      name: "phase_type",
      options: typeOptions,
    },
    {
      label: "Cycle",
      type: "select",
      multiple: true,
      name: "cycle",
      placeholder: "Select Cycle",
      options: cycleOptions,
      onChange: handleCycleChange,
    },
    {
      label: "Hospital/Site",
      type: "select",
      // name: 'hospitals',
      name: "hospital_id",
      placeholder: "Select Hospital/Site",
      options: hospitalOptions,
    },
    {
      label: 'Payment Status',
      type: 'select',
      name: 'payment_status',
      placeholder: 'Select Payment Status',
      options: PaymentStatusList,
    }
  ];

const handleSubmit = (values) => {
  const product_id = values.product_id
    ? (Array.isArray(values.product_id) ? values.product_id : [values.product_id])
    : [];

    const hospital_id = values.hospital_id
      ? Array.isArray(values.hospital_id)
        ? values.hospital_id
        : [values.hospital_id]
      : [];
const payment_status = values.payment_status
  ? (Array.isArray(values.payment_status) ? values.payment_status : [values.payment_status])
  : [];
    const product_id_labels = product_id
      .map((id) => productOptions.find((p) => p.value === id)?.label)
      .filter(Boolean);

    const hospital_id_labels = hospital_id
      .map((id) => hospitalOptions.find((h) => h.value === id)?.label)
      .filter(Boolean);
const payment_status_labels = payment_status
  .map((id) => PaymentStatusList?.find((p) => p.value === id)?.label)
  .filter(Boolean);

    onApply({
      ...values,
      product_id,
      hospital_id,
      payment_status,
      product_id_labels,
      hospital_id_labels,
      payment_status_labels,
    });
  };
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const handleClear = () => {
    formik.setValues({
      product_id: "",
      phase_type: [],
      cycle: [],
      hospital_id: "",
      payment_status: [],
    });
    onClear();
  };

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        {formOptions.map((props, key) => (
          <Grid key={key} item xs={12}>
            <FormikControl formik={formik} {...props} />
          </Grid>
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            marginTop: "10px",
          }}
        >
          <LoadingButton
            size="large"
            sx={{ borderRadius: "6px" }}
            onClick={handleClear}
            variant="outlined"
          >
            Clear Filter
          </LoadingButton>
          <LoadingButton
            size="large"
            sx={{ borderRadius: "6px" }}
            type="submit"
            variant="contained"
          >
            Apply Filter
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default PaymentdashboardFilter;
